/**
 * Soltik public homepage — people-first social positioning.
 *
 * The platform is deliberately invite-only during its cold start. Marketing
 * CTAs therefore build the founding-community waitlist instead of linking to
 * open signup. Example profiles and posts are visibly labelled as illustrative
 * until real, permissioned community stories can replace them.
 */
import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  Bookmark,
  Check,
  Compass,
  DollarSign,
  Heart,
  MessageCircle,
  Radio,
  Send,
  ShieldCheck,
  Sparkles,
  Store,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Link from '@/components/LocaleLink';
import { currentYear } from '../../lib/currentYear';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const SOCIAL_PROOF_MIN = 250;
const HERO_IMAGE = '/marketing/social/creator-community-hero-v2.webp';

const COMMUNITIES = [
  { key: 'makers', label: 'Indie makers', tone: 'ochre', avatars: ['a2', 'a4'] },
  { key: 'ceramics', label: 'Ceramics', tone: 'clay', avatars: ['a1', 'a2'] },
  { key: 'music', label: 'Music', tone: 'blue', avatars: ['a4', 'a3'] },
  { key: 'illustration', label: 'Illustration', tone: 'cream', avatars: ['a3', 'a1'] },
  { key: 'local', label: 'Local creators', tone: 'green', avatars: ['a2', 'a3'] },
  { key: 'film', label: 'Film & photo', tone: 'rose', avatars: ['a4', 'a1'] },
];

const SOCIAL_PROMISES = [
  {
    key: 'rooms',
    Icon: Users,
    title: 'Communities with a reason to talk',
    body: 'Join rooms built around what you make—not engagement bait. Share progress, ask questions, and learn in public.',
  },
  {
    key: 'collaborators',
    Icon: MessageCircle,
    title: 'Meet collaborators, not followers',
    body: 'Replies become conversations. Conversations become projects, friendships, customers, and creative momentum.',
  },
  {
    key: 'ownership',
    Icon: ShieldCheck,
    title: 'Your work stays yours',
    body: 'Build a profile, an audience, and a home you control. Export your published work and subscriber data whenever you want.',
  },
];

const CREATOR_TOOLS = [
  { key: 'home', Icon: Store, title: 'Your home on the web', body: 'Publish a profile, site, courses, and a shop without stitching five tools together.' },
  { key: 'earn', Icon: DollarSign, title: 'Earn when you are ready', body: 'Turn on tips, subscriptions, paid posts, or products without changing platforms.' },
  { key: 'ai', Icon: Sparkles, title: 'AI that stays in the background', body: 'Get help drafting and planning. You approve everything, and assisted posts are disclosed.' },
];

export default function AgenticLanding() {
  const { t, i18n } = useTranslation();
  const [requestCount, setRequestCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('authToken'));
    fetch(`${API_URL}/waitlist/count`)
      .then((response) => response.json())
      .then((data) => {
        const count = Number(data?.count);
        setRequestCount(Number.isFinite(count) && count > 0 ? Math.floor(count) : 0);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.title = `${t('agenticLanding.social.heroLine1', 'Share what you make.')} ${t('agenticLanding.social.heroLine2', 'Find your people.')} — Soltik`;
    document
      .querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute('content', t('agenticLanding.social.lead', 'Post your work, join communities, meet collaborators, and build a direct audience around what you make.'));
  }, [i18n.resolvedLanguage, t]);

  const ctaTarget = isLoggedIn ? '/dashboard' : '/request-invite';
  const ctaLabel = isLoggedIn
    ? t('agenticLanding.openApp', 'Open app')
    : t('agenticLanding.social.requestInvite', 'Request an invite');

  const scrollTo = (id: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const target = document.getElementById(id);
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  };

  return (
    <div className="sk-root min-h-screen overflow-x-hidden">
      <style>{STYLES}</style>

      <a href="#main-content" className="sk-skip">
        {t('agenticLanding.skipToContent', 'Skip to content')}
      </a>

      <header className="sk-header">
        <div className="sk-container sk-header-inner">
          <Link to="/" className="sk-brand" aria-label={t('agenticLanding.social.homeLabel', 'Soltik home')}>
            <img src="/logo-yellow.png" alt="" className="sk-brand-img" width={32} height={32} />
            <span className="sk-brand-word">soltik</span>
          </Link>

          <nav className="sk-nav" aria-label={t('agenticLanding.social.primaryNav', 'Primary navigation')}>
            <a href="#activity" onClick={scrollTo('activity')} className="sk-nav-link sk-hide-tablet">
              {t('agenticLanding.social.navDiscover', 'Discover')}
            </a>
            <a href="#community" onClick={scrollTo('community')} className="sk-nav-link sk-hide-tablet">
              {t('agenticLanding.social.navCommunities', 'Communities')}
            </a>
            <Link to="/for-creators" className="sk-nav-link sk-hide-sm">
              {t('agenticLanding.social.navCreators', 'For creators')}
            </Link>
            {!isLoggedIn && (
              <Link to="/login" className="sk-nav-link sk-hide-sm">
                {t('agenticLanding.signIn', 'Sign in')}
              </Link>
            )}
            <Link to={ctaTarget} className="sk-btn sk-btn-primary sk-btn-sm">
              {ctaLabel}
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </nav>
        </div>
      </header>

      <main id="main-content">
        <section className="sk-hero">
          <div className="sk-container sk-hero-grid">
            <div className="sk-hero-copy">
              <div className="sk-badge sk-reveal">
                <span className="sk-dot" aria-hidden="true" />
                {requestCount >= SOCIAL_PROOF_MIN
                  ? t('agenticLanding.social.waitlistCount', '{count} people requested early access', { count: requestCount })
                  : t('agenticLanding.social.inviteOnly', 'Founding community · invite-only')}
              </div>

              <p className="sk-eyebrow sk-reveal sk-d1">
                {t('agenticLanding.social.eyebrow', 'A social home for people who make things')}
              </p>
              <h1 className="sk-h1">
                {t('agenticLanding.social.heroLine1', 'Share what you make.')}
                <br />
                <em>{t('agenticLanding.social.heroLine2', 'Find your people.')}</em>
              </h1>
              <p className="sk-lead sk-reveal sk-d2">
                {t('agenticLanding.social.lead', 'Post your work, join communities, meet collaborators, and build a direct audience around what you make.')}
              </p>

              <div className="sk-cta-row sk-reveal sk-d3">
                <Link to={ctaTarget} className="sk-btn sk-btn-primary sk-btn-lg">
                  {ctaLabel}
                  <ArrowRight size={17} aria-hidden="true" />
                </Link>
                <a href="#community" onClick={scrollTo('community')} className="sk-btn sk-btn-ghost sk-btn-lg">
                  {t('agenticLanding.social.exploreCommunities', 'Explore communities')}
                </a>
              </div>

              <div className="sk-proof sk-reveal sk-d4">
                <span><Check size={13} aria-hidden="true" /> {t('agenticLanding.social.proofBeta', 'Free during beta')}</span>
                <span><Check size={13} aria-hidden="true" /> {t('agenticLanding.social.proofNoCard', 'No credit card')}</span>
                <span><Check size={13} aria-hidden="true" /> {t('agenticLanding.social.proofOwned', 'Your work stays yours')}</span>
              </div>
            </div>

            <div className="sk-hero-media">
              <img
                src={HERO_IMAGE}
                alt={t('agenticLanding.social.heroImageAlt', 'Five creators sharing digital work and ideas around a table')}
                width={1536}
                height={1024}
                fetchPriority="high"
              />
              <div className="sk-photo-wash" aria-hidden="true" />
              <div className="sk-live-card" aria-label={t('agenticLanding.social.previewPostLabel', 'Illustrative Soltik post preview')}>
                <span className="sk-preview-label">
                  {t('agenticLanding.social.illustrative', 'Illustrative community preview')}
                </span>
                <div className="sk-post-head">
                  <img src="/avatars/a1.webp" alt="" width={34} height={34} />
                  <div>
                    <strong>{t('agenticLanding.social.heroPostName', 'Maya')}</strong>
                    <span>{t('agenticLanding.social.heroPostMeta', 'Indie makers · launch diary')}</span>
                  </div>
                  <span className="sk-preview-chip">{t('agenticLanding.social.aiAssist', 'AI assisted')}</span>
                </div>
                <p>{t('agenticLanding.social.heroPostBody', 'Sharing the first cut of our launch story. The ending still feels flat—what would you change?')}</p>
                <div className="sk-ai-note">
                  <Sparkles size={12} aria-hidden="true" />
                  {t('agenticLanding.social.aiNote', 'Drafted from Maya’s notes · creator approved')}
                </div>
                <div className="sk-post-actions" aria-hidden="true">
                  <span><Heart size={14} /></span>
                  <span><MessageCircle size={14} /></span>
                  <span className="sk-bookmark"><Bookmark size={14} /></span>
                </div>
                <div className="sk-comment">
                  <img src="/avatars/a2.webp" alt="" width={22} height={22} />
                  <span><b>{t('agenticLanding.social.commentName', 'Jon')}</b> {t('agenticLanding.social.commentBody', 'Keep the quiet moment at the end. It feels honest.')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="community" className="sk-community-section" aria-labelledby="community-title">
          <div className="sk-container">
            <div className="sk-section-heading sk-section-heading-row">
              <div>
                <p className="sk-eyebrow">{t('agenticLanding.social.communityEyebrow', 'Communities')}</p>
                <h2 id="community-title" className="sk-h2">{t('agenticLanding.social.communityTitle', 'Find your corner.')}</h2>
              </div>
              <Link to={ctaTarget} className="sk-text-link">
                {isLoggedIn
                  ? t('agenticLanding.openApp', 'Open app')
                  : t('agenticLanding.social.communityCta', 'Request access to all communities')}
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>

            <div className="sk-community-grid">
              {COMMUNITIES.map((community) => (
                <article key={community.key} className={`sk-community-card sk-tone-${community.tone}`}>
                  <Compass size={18} aria-hidden="true" />
                  <div className="sk-community-avatars" aria-hidden="true">
                    {community.avatars.map((avatar) => (
                      <img key={avatar} src={`/avatars/${avatar}.webp`} alt="" width={30} height={30} loading="lazy" decoding="async" />
                    ))}
                  </div>
                  <h3>{t(`agenticLanding.social.communities.${community.key}`, community.label)}</h3>
                  <span>{t('agenticLanding.social.foundingRoom', 'Founding room · invite-only')}</span>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="activity" className="sk-section" aria-labelledby="activity-title">
          <div className="sk-container">
            <div className="sk-section-heading">
              <p className="sk-eyebrow">{t('agenticLanding.social.activityEyebrow', 'A look inside')}</p>
              <h2 id="activity-title" className="sk-h2">
                {t('agenticLanding.social.activityTitle', 'What Soltik should feel like.')}
              </h2>
              <p className="sk-section-lead">
                {t('agenticLanding.social.activityLead', 'Less performance. More work in progress, useful replies, and people who remember your name.')}
              </p>
              <span className="sk-illustrative-label">
                {t('agenticLanding.social.illustrative', 'Illustrative community preview')}
              </span>
            </div>

            <div className="sk-feed-grid">
              <article className="sk-feed-card">
                <PostHeader avatar="a2" name="Ben" meta={t('agenticLanding.social.posts.makerMeta', 'Indie makers · 1h')} />
                <p>{t('agenticLanding.social.posts.makerBody', 'Prototype for a clamp-on desk shelf I have been building. Thoughts before I batch these?')}</p>
                <div className="sk-feed-visual sk-feed-photo" role="img" aria-label={t('agenticLanding.social.posts.makerImageAlt', 'A creator workshop in progress')} />
                <PostFooter likes="18" replies="7" />
                <div className="sk-comment sk-comment-card">
                  <img src="/avatars/a4.webp" alt="" width={24} height={24} loading="lazy" />
                  <span><b>{t('agenticLanding.social.posts.makerCommentName', 'Noah')}</b> {t('agenticLanding.social.posts.makerComment', 'Could the back rail double as cable management?')}</span>
                </div>
              </article>

              <article className="sk-feed-card">
                <PostHeader avatar="a3" name="Aya" meta={t('agenticLanding.social.posts.musicMeta', 'Music · 3h')} />
                <p>{t('agenticLanding.social.posts.musicBody', 'Working on a lo-fi beat inspired by rainy train rides. This is the rough first minute.')}</p>
                <div className="sk-audio-preview" aria-label={t('agenticLanding.social.posts.audioLabel', 'Audio post preview')}>
                  <span className="sk-play">▶</span>
                  <span>0:00 / 0:30</span>
                  <span className="sk-wave" aria-hidden="true" />
                </div>
                <PostFooter likes="31" replies="12" />
                <div className="sk-comment sk-comment-card">
                  <img src="/avatars/a1.webp" alt="" width={24} height={24} loading="lazy" />
                  <span><b>{t('agenticLanding.social.posts.musicCommentName', 'Maya')}</b> {t('agenticLanding.social.posts.musicComment', 'That texture at 0:18 is lovely.')}</span>
                </div>
              </article>

              <article className="sk-feed-card">
                <PostHeader avatar="a4" name="Inkwell" meta={t('agenticLanding.social.posts.artMeta', 'Illustration · 5h')} />
                <p>{t('agenticLanding.social.posts.artBody', 'New zine cover in progress. Trying to lean into texture and quiet details.')}</p>
                <div className="sk-feed-visual sk-sketch" role="img" aria-label={t('agenticLanding.social.posts.artImageAlt', 'Botanical illustration in progress')}>
                  <svg viewBox="0 0 360 190" fill="none" aria-hidden="true">
                    <path d="M177 184c8-47 3-91-13-143M168 104c-33-12-54-31-66-59M171 126c35-10 60-30 76-61M163 79c21-14 32-30 35-49" stroke="currentColor" strokeWidth="2" />
                    <path d="M102 45c24-5 43 2 57 23-25 2-44-5-57-23ZM247 65c-4 24-19 41-44 50 5-25 19-41 44-50ZM198 30c17 9 25 23 24 42-18-9-26-23-24-42ZM107 121c22-8 41-3 58 15-23 6-42 1-58-15Z" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <PostFooter likes="24" replies="9" />
              </article>
            </div>
          </div>
        </section>

        <section className="sk-section sk-section-warm" aria-labelledby="connection-title">
          <div className="sk-container">
            <p className="sk-eyebrow">{t('agenticLanding.social.whyEyebrow', 'Why Soltik')}</p>
            <h2 id="connection-title" className="sk-h2">
              {t('agenticLanding.social.whyTitle', 'Built for connection,')}{' '}
              <em>{t('agenticLanding.social.whyTitleEm', 'not attention.')}</em>
            </h2>
            <div className="sk-promise-grid">
              {SOCIAL_PROMISES.map(({ key, Icon, title, body }) => (
                <article key={key} className="sk-promise-card">
                  <span className="sk-icon"><Icon size={20} aria-hidden="true" /></span>
                  <h3>{t(`agenticLanding.social.promises.${key}.title`, title)}</h3>
                  <p>{t(`agenticLanding.social.promises.${key}.body`, body)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sk-section" aria-labelledby="tools-title">
          <div className="sk-container sk-tools-layout">
            <div className="sk-tools-copy">
              <p className="sk-eyebrow">{t('agenticLanding.social.toolsEyebrow', 'More than a feed')}</p>
              <h2 id="tools-title" className="sk-h2">
                {t('agenticLanding.social.toolsTitle', 'Start with people.')}{' '}
                <em>{t('agenticLanding.social.toolsTitleEm', 'Grow into everything else.')}</em>
              </h2>
              <p className="sk-section-lead">
                {t('agenticLanding.social.toolsLead', 'Your community is the beginning. When you are ready, Soltik also gives your work a home, a business, and a thoughtful creative assistant.')}
              </p>
              <Link to="/for-creators" className="sk-text-link">
                {t('agenticLanding.social.toolsCta', 'See everything creators can build')}
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </div>
            <div className="sk-tool-list">
              {CREATOR_TOOLS.map(({ key, Icon, title, body }) => (
                <article key={key} className="sk-tool-row">
                  <span className="sk-icon sk-icon-dark"><Icon size={18} aria-hidden="true" /></span>
                  <div>
                    <h3>{t(`agenticLanding.social.tools.${key}.title`, title)}</h3>
                    <p>{t(`agenticLanding.social.tools.${key}.body`, body)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="sk-final-section">
          <div className="sk-container">
            <div className="sk-final-card">
              <div className="sk-final-copy">
                <span className="sk-final-icon"><Radio size={22} aria-hidden="true" /></span>
                <p className="sk-eyebrow">{t('agenticLanding.social.finalEyebrow', 'Founding community')}</p>
                <h2>{t('agenticLanding.social.finalTitle', 'Help shape the room before the doors open.')}</h2>
                <p>
                  {t('agenticLanding.social.finalBody', 'We release invitations in small groups so every new member arrives to real conversations—not an empty feed. Tell us what you make and where you want to belong.')}
                </p>
              </div>
              <div className="sk-final-action">
                <Link to={ctaTarget} className="sk-btn sk-btn-primary sk-btn-lg">
                  {ctaLabel}
                  <ArrowRight size={17} aria-hidden="true" />
                </Link>
                <span>{t('agenticLanding.social.finalFine', 'Free during beta · Invitations released in small groups')}</span>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="sk-footer">
        <div className="sk-container sk-footer-inner">
          <div className="sk-brand sk-brand-sm">
            <img src="/logo-yellow.png" alt="" className="sk-brand-img sk-brand-img-sm" width={24} height={24} loading="lazy" />
            <span>© {currentYear()} Braineetech LLC</span>
          </div>
          <div className="sk-footer-links">
            <Link to="/security">{t('agenticLanding.security', 'Security')}</Link>
            <Link to="/privacy">{t('agenticLanding.privacy', 'Privacy')}</Link>
            <Link to="/cookies">{t('agenticLanding.cookies', 'Cookies')}</Link>
            <Link to="/tos">{t('agenticLanding.terms', 'Terms')}</Link>
            <Link to="/content-policy">{t('agenticLanding.policy', 'Content policy')}</Link>
            <Link to="/imprint">{t('agenticLanding.imprintLink', 'Imprint')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PostHeader({ avatar, name, meta }: { avatar: string; name: string; meta: string }) {
  return (
    <div className="sk-post-head">
      <img src={`/avatars/${avatar}.webp`} alt="" width={36} height={36} loading="lazy" decoding="async" />
      <div>
        <strong>{name}</strong>
        <span>{meta}</span>
      </div>
      <span className="sk-more" aria-hidden="true">•••</span>
    </div>
  );
}

function PostFooter({ likes, replies }: { likes: string; replies: string }) {
  return (
    <div className="sk-post-actions" aria-hidden="true">
      <span><Heart size={15} /> {likes}</span>
      <span><MessageCircle size={15} /> {replies}</span>
      <span><Send size={15} /></span>
      <span className="sk-bookmark"><Bookmark size={15} /></span>
    </div>
  );
}

const STYLES = `
  .sk-root{
    --paper:#0B0B10; --paper-2:#141419; --paper-3:#1B1B21;
    --ink:#F5F3EE; --ink-2:rgba(245,243,238,.72); --ink-3:rgba(245,243,238,.54);
    --line:rgba(255,255,255,.11); --line-soft:rgba(255,255,255,.065);
    --acid:#E6EA3A; --acid-soft:rgba(230,234,58,.12); --acid-ink:#F0F36A;
    --warm:#D79868; --cream:#EEE5D7; --blue:#7891A8; --green:#6F8A72;
    --font-body:'Plus Jakarta Sans','DM Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
    --font-display:'Instrument Serif','Playfair Display',Georgia,serif;
    min-height:100vh; background:var(--paper); color:var(--ink); font-family:var(--font-body);
    -webkit-font-smoothing:antialiased;
  }
  .sk-root *{ box-sizing:border-box; }
  .sk-root ::selection{ background:var(--acid); color:#0B0B10; }
  .sk-container{ width:min(1240px,calc(100% - 48px)); margin-inline:auto; }
  .sk-skip{ position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
  .sk-skip:focus{ position:fixed; width:auto; height:auto; clip:auto; overflow:visible; inset-inline-start:12px; top:12px; z-index:70; background:var(--acid); color:#0B0B10; padding:10px 16px; border-radius:10px; font-weight:800; }
  .sk-root a:focus-visible{ outline:3px solid var(--acid); outline-offset:4px; border-radius:8px; }

  .sk-header{ position:sticky; top:0; z-index:50; background:rgba(11,11,16,.82); backdrop-filter:saturate(1.25) blur(16px); border-bottom:1px solid var(--line-soft); }
  .sk-header-inner{ min-height:68px; display:flex; align-items:center; justify-content:space-between; gap:24px; }
  .sk-brand{ display:inline-flex; align-items:center; gap:9px; color:var(--ink); text-decoration:none; flex:none; }
  .sk-brand-img{ width:32px; height:32px; border-radius:8px; display:block; }
  .sk-brand-word{ font-family:var(--font-display); font-size:26px; letter-spacing:.01em; }
  .sk-nav{ display:flex; align-items:center; gap:25px; }
  .sk-nav-link{ color:var(--ink-2); text-decoration:none; font-size:14px; font-weight:550; transition:color .2s ease; }
  .sk-nav-link:hover{ color:var(--ink); }

  .sk-btn{ min-height:44px; display:inline-flex; align-items:center; justify-content:center; gap:8px; border:1px solid transparent; border-radius:11px; padding:0 20px; text-decoration:none; font-weight:750; cursor:pointer; transition:background .2s ease,color .2s ease,border-color .2s ease,box-shadow .2s ease,transform .2s ease; }
  .sk-btn-primary{ background:var(--acid); color:#0B0B10; box-shadow:0 8px 26px rgba(230,234,58,.18); }
  .sk-btn-primary:hover{ background:#F1F45D; box-shadow:0 12px 34px rgba(230,234,58,.27); transform:translateY(-1px); }
  .sk-btn-ghost{ color:var(--ink); border-color:var(--line); background:rgba(255,255,255,.015); }
  .sk-btn-ghost:hover{ background:rgba(255,255,255,.07); border-color:rgba(255,255,255,.2); }
  [dir="rtl"] .sk-btn svg,[dir="rtl"] .sk-text-link svg{ transform:rotate(180deg); }
  .sk-btn-sm{ min-height:40px; padding-inline:16px; font-size:13.5px; }
  .sk-btn-lg{ min-height:52px; padding-inline:24px; font-size:15px; }

  .sk-hero{ padding:52px 0 58px; border-bottom:1px solid var(--line-soft); }
  .sk-hero-grid{ display:grid; grid-template-columns:minmax(360px,.78fr) minmax(0,1.22fr); align-items:center; gap:44px; }
  .sk-hero-copy{ position:relative; z-index:2; padding-block:32px; }
  .sk-badge{ width:max-content; max-width:100%; display:flex; align-items:center; gap:9px; margin-bottom:26px; padding:7px 13px; border:1px solid rgba(230,234,58,.23); background:var(--acid-soft); border-radius:999px; color:var(--acid-ink); font-size:12.5px; font-weight:700; }
  .sk-dot{ width:7px; height:7px; flex:none; border-radius:50%; background:var(--acid); box-shadow:0 0 0 0 rgba(230,234,58,.5); animation:skPulse 2s infinite; }
  .sk-eyebrow{ margin:0 0 13px; color:var(--acid-ink); font-size:12px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; }
  .sk-h1{ margin:0 0 22px; font-family:var(--font-display); font-size:clamp(54px,5.35vw,82px); font-weight:400; line-height:.96; letter-spacing:-.025em; }
  .sk-h1 em,.sk-h2 em{ color:var(--acid); font-style:italic; font-weight:400; }
  .sk-lead{ max-width:590px; margin:0 0 29px; color:var(--ink-2); font-size:clamp(17px,1.35vw,20px); line-height:1.62; }
  .sk-cta-row{ display:flex; flex-wrap:wrap; gap:11px; margin-bottom:20px; }
  .sk-proof{ display:flex; flex-wrap:wrap; gap:15px; color:var(--ink-3); font-size:12.5px; }
  .sk-proof span{ display:inline-flex; align-items:center; gap:5px; }
  .sk-proof svg{ color:var(--acid); }

  .sk-hero-media{ position:relative; min-height:610px; overflow:hidden; border-radius:22px; border:1px solid var(--line); background:#211B15; box-shadow:0 30px 90px rgba(0,0,0,.4); }
  .sk-hero-media>img{ width:100%; height:100%; position:absolute; inset:0; object-fit:cover; object-position:center; }
  .sk-photo-wash{ position:absolute; inset:0; background:linear-gradient(90deg,rgba(11,11,16,.3),transparent 34%),linear-gradient(0deg,rgba(11,11,16,.3),transparent 42%); pointer-events:none; }
  .sk-live-card{ direction:inherit; text-align:start; position:absolute; width:min(310px,calc(100% - 28px)); inset-inline-end:18px; bottom:18px; padding:15px; border:1px solid rgba(255,255,255,.16); border-radius:15px; background:rgba(14,14,18,.92); backdrop-filter:blur(15px); box-shadow:0 18px 45px rgba(0,0,0,.42); }
  .sk-preview-label{ display:block; margin:0 0 10px; color:var(--ink-3); font-size:9px; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
  .sk-post-head{ display:flex; align-items:center; gap:10px; min-width:0; }
  .sk-post-head img{ width:36px; height:36px; border-radius:50%; object-fit:cover; flex:none; }
  .sk-post-head>div{ display:flex; flex-direction:column; min-width:0; }
  .sk-post-head strong{ color:var(--ink); font-size:13px; line-height:1.3; }
  .sk-post-head span{ color:var(--ink-3); font-size:10.5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .sk-preview-chip{ margin-inline-start:auto; padding:4px 7px; border-radius:999px; background:var(--acid-soft); color:var(--acid-ink)!important; font-size:9px!important; font-weight:800; text-transform:uppercase; letter-spacing:.07em; }
  .sk-live-card>p{ margin:13px 0; color:var(--ink); font-size:12px; line-height:1.48; }
  .sk-ai-note{ display:flex; align-items:center; gap:6px; margin:-2px 0 11px; color:var(--acid-ink); font-size:9.5px; }
  .sk-post-actions{ display:flex; align-items:center; gap:17px; padding:10px 0; color:var(--ink-3); font-size:11px; border-top:1px solid var(--line-soft); border-bottom:1px solid var(--line-soft); }
  .sk-post-actions span{ display:inline-flex; align-items:center; gap:5px; }
  .sk-post-actions .sk-bookmark{ margin-inline-start:auto; }
  .sk-comment{ display:flex; align-items:flex-start; gap:8px; margin-top:10px; color:var(--ink-2); font-size:10.5px; line-height:1.4; }
  .sk-comment img{ width:22px; height:22px; border-radius:50%; object-fit:cover; flex:none; }
  .sk-comment b{ color:var(--ink); margin-inline-end:4px; }

  .sk-community-section{ padding:56px 0 64px; border-bottom:1px solid var(--line-soft); background:#0E0E13; scroll-margin-top:78px; }
  .sk-section{ padding:94px 0; border-bottom:1px solid var(--line-soft); scroll-margin-top:78px; }
  .sk-section-heading{ position:relative; margin-bottom:35px; }
  .sk-section-heading-row{ display:flex; align-items:flex-end; justify-content:space-between; gap:24px; }
  .sk-h2{ max-width:800px; margin:0; font-family:var(--font-display); font-size:clamp(40px,4.8vw,66px); line-height:1.02; font-weight:400; letter-spacing:-.02em; }
  .sk-section-lead{ max-width:650px; margin:17px 0 0; color:var(--ink-2); font-size:17px; line-height:1.65; }
  .sk-text-link{ min-height:44px; display:inline-flex; align-items:center; gap:7px; color:var(--acid-ink); text-decoration:none; font-size:13px; font-weight:750; transition:gap .2s ease,color .2s ease; }
  .sk-text-link:hover{ gap:11px; color:var(--acid); }
  .sk-illustrative-label{ display:inline-flex; margin-top:15px; padding:5px 9px; border:1px solid var(--line); border-radius:999px; color:var(--ink-3); font-size:10.5px; letter-spacing:.04em; text-transform:uppercase; }

  .sk-community-grid{ display:grid; grid-template-columns:repeat(6,minmax(0,1fr)); gap:12px; }
  .sk-community-card{ min-height:170px; position:relative; overflow:hidden; display:flex; flex-direction:column; justify-content:flex-end; padding:17px; border:1px solid rgba(255,255,255,.12); border-radius:15px; isolation:isolate; transition:border-color .2s ease,transform .2s ease; }
  .sk-community-card:hover{ transform:translateY(-2px); border-color:rgba(230,234,58,.35); }
  .sk-community-card::before{ content:''; position:absolute; width:120px; height:120px; border-radius:50%; top:-50px; inset-inline-end:-35px; background:rgba(255,255,255,.1); filter:blur(1px); z-index:-1; }
  .sk-community-card>svg{ position:absolute; top:16px; inset-inline-start:16px; color:rgba(255,255,255,.75); }
  .sk-tone-ochre{ background:linear-gradient(145deg,#322817,#17150F); }
  .sk-tone-clay{ background:linear-gradient(145deg,#503223,#201713); }
  .sk-tone-blue{ background:linear-gradient(145deg,#263643,#14191E); }
  .sk-tone-cream{ background:linear-gradient(145deg,#4B453B,#211F1B); }
  .sk-tone-green{ background:linear-gradient(145deg,#29372C,#151B17); }
  .sk-tone-rose{ background:linear-gradient(145deg,#422A31,#1C1518); }
  .sk-community-avatars{ display:flex; margin-bottom:11px; }
  .sk-community-avatars img{ width:30px; height:30px; object-fit:cover; border:2px solid rgba(11,11,16,.85); border-radius:50%; margin-inline-start:-8px; }
  .sk-community-avatars img:first-child{ margin-inline-start:0; }
  .sk-community-card h3{ margin:0 0 4px; font-size:14px; font-weight:750; }
  .sk-community-card>span{ color:var(--ink-3); font-size:10.5px; }

  .sk-feed-grid{ display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:18px; }
  .sk-feed-card{ min-width:0; padding:17px; border:1px solid var(--line); border-radius:17px; background:var(--paper-2); }
  .sk-feed-card>p{ min-height:52px; margin:15px 0; color:var(--ink-2); font-size:13.5px; line-height:1.5; }
  .sk-more{ margin-inline-start:auto; color:var(--ink-3); letter-spacing:2px; }
  .sk-feed-visual{ height:190px; margin:0 0 13px; overflow:hidden; border-radius:11px; background:var(--paper-3); }
  .sk-feed-photo{ background-image:linear-gradient(0deg,rgba(10,10,14,.14),rgba(10,10,14,.02)),url('${HERO_IMAGE}'); background-size:cover; background-position:18% 63%; }
  .sk-sketch{ display:grid; place-items:center; color:#4A3828; background:linear-gradient(145deg,#E8DAC6,#B99C7B); }
  .sk-sketch svg{ width:78%; height:82%; transform:rotate(-2deg); filter:drop-shadow(0 5px 8px rgba(35,22,12,.15)); }
  .sk-audio-preview{ height:190px; display:flex; align-items:center; gap:11px; margin-bottom:13px; padding:20px; border-radius:11px; background:radial-gradient(circle at 80% 20%,rgba(120,145,168,.28),transparent 44%),#10151A; color:var(--ink-2); font-size:11px; }
  .sk-play{ width:40px; height:40px; display:grid; place-items:center; flex:none; border-radius:50%; background:var(--acid); color:#0B0B10; padding-inline-start:2px; }
  .sk-wave{ height:58px; flex:1; opacity:.6; background:repeating-linear-gradient(90deg,var(--blue) 0 2px,transparent 2px 6px); mask-image:linear-gradient(0deg,transparent 0,black 20%,black 80%,transparent 100%); clip-path:polygon(0 46%,5% 35%,10% 60%,15% 25%,20% 70%,25% 40%,30% 55%,35% 10%,40% 80%,45% 30%,50% 65%,55% 20%,60% 75%,65% 38%,70% 62%,75% 15%,80% 85%,85% 35%,90% 58%,95% 28%,100% 50%,100% 55%,95% 40%,90% 68%,85% 45%,80% 92%,75% 25%,70% 70%,65% 48%,60% 82%,55% 30%,50% 75%,45% 40%,40% 88%,35% 20%,30% 65%,25% 50%,20% 78%,15% 35%,10% 68%,5% 45%,0 55%); }
  .sk-comment-card{ min-height:44px; }
  .sk-comment-card img{ width:24px; height:24px; }

  .sk-section-warm{ background:#12110E; }
  .sk-promise-grid{ display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:16px; margin-top:44px; }
  .sk-promise-card{ padding:26px; border:1px solid var(--line); border-radius:17px; background:rgba(255,255,255,.025); }
  .sk-icon{ width:44px; height:44px; display:grid; place-items:center; margin-bottom:20px; border-radius:13px; color:#0B0B10; background:var(--acid); }
  .sk-promise-card h3,.sk-tool-row h3{ margin:0 0 8px; font-size:18px; letter-spacing:-.01em; }
  .sk-promise-card p,.sk-tool-row p{ margin:0; color:var(--ink-2); font-size:14px; line-height:1.65; }

  .sk-tools-layout{ display:grid; grid-template-columns:.88fr 1.12fr; gap:80px; align-items:start; }
  .sk-tools-copy{ position:sticky; top:110px; }
  .sk-tools-copy .sk-text-link{ margin-top:22px; }
  .sk-tool-list{ display:flex; flex-direction:column; gap:13px; }
  .sk-tool-row{ display:flex; gap:17px; padding:21px; border:1px solid var(--line); border-radius:16px; background:var(--paper-2); }
  .sk-icon-dark{ width:42px; height:42px; flex:none; margin:0; background:var(--acid-soft); color:var(--acid-ink); }

  .sk-final-section{ padding:36px 0 100px; }
  .sk-final-card{ display:grid; grid-template-columns:1fr auto; gap:54px; align-items:end; padding:54px; overflow:hidden; position:relative; border:1px solid rgba(230,234,58,.22); border-radius:24px; background:radial-gradient(circle at 88% 15%,rgba(230,234,58,.14),transparent 28%),var(--paper-2); }
  .sk-final-copy{ max-width:760px; }
  .sk-final-icon{ width:46px; height:46px; display:grid; place-items:center; margin-bottom:30px; border-radius:14px; background:var(--acid); color:#0B0B10; }
  .sk-final-card h2{ margin:0 0 17px; font-family:var(--font-display); font-size:clamp(40px,4.5vw,62px); line-height:1.02; font-weight:400; letter-spacing:-.02em; }
  .sk-final-copy>p:last-child{ max-width:680px; margin:0; color:var(--ink-2); font-size:16px; line-height:1.65; }
  .sk-final-action{ display:flex; flex-direction:column; align-items:center; gap:13px; min-width:220px; }
  .sk-final-action>span{ color:var(--ink-3); font-size:10.5px; text-align:center; }

  .sk-footer{ padding:39px 0; border-top:1px solid var(--line-soft); background:#09090D; }
  .sk-footer-inner{ display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:18px; color:var(--ink-3); font-size:12px; }
  .sk-brand-sm{ gap:9px; }
  .sk-brand-img-sm{ width:24px; height:24px; }
  .sk-footer-links{ display:flex; flex-wrap:wrap; gap:18px; }
  .sk-footer-links a{ color:var(--ink-3); text-decoration:none; padding-block:7px; }
  .sk-footer-links a:hover{ color:var(--ink); }

  .sk-reveal{ animation:skReveal .7s cubic-bezier(.2,.7,.2,1) both; }
  .sk-d1{ animation-delay:.07s; } .sk-d2{ animation-delay:.14s; } .sk-d3{ animation-delay:.21s; } .sk-d4{ animation-delay:.28s; }
  @keyframes skReveal{ from{ opacity:0; transform:translateY(13px); } to{ opacity:1; transform:none; } }
  @keyframes skPulse{ 0%{ box-shadow:0 0 0 0 rgba(230,234,58,.42); } 70%{ box-shadow:0 0 0 8px rgba(230,234,58,0); } 100%{ box-shadow:0 0 0 0 rgba(230,234,58,0); } }

  @media(max-width:1050px){
    .sk-hero-grid{ grid-template-columns:.9fr 1.1fr; gap:28px; }
    .sk-hero-media{ min-height:540px; }
    .sk-community-grid{ grid-template-columns:repeat(3,1fr); }
    .sk-community-card{ min-height:150px; }
  }
  @media(max-width:880px){
    .sk-hide-tablet{ display:none; }
    .sk-hero{ padding-top:28px; }
    .sk-hero-grid{ grid-template-columns:1fr; }
    .sk-hero-copy{ padding-bottom:4px; }
    .sk-hero-media{ min-height:520px; }
    .sk-feed-grid,.sk-promise-grid{ grid-template-columns:1fr 1fr; }
    .sk-feed-card:last-child,.sk-promise-card:last-child{ grid-column:1/-1; }
    .sk-tools-layout{ grid-template-columns:1fr; gap:40px; }
    .sk-tools-copy{ position:static; }
    .sk-final-card{ grid-template-columns:1fr; align-items:start; gap:32px; }
    .sk-final-action{ align-items:flex-start; }
  }
  @media(max-width:620px){
    .sk-container{ width:min(100% - 32px,1240px); }
    .sk-header-inner{ min-height:62px; }
    .sk-hide-sm{ display:none; }
    .sk-nav{ gap:10px; }
    .sk-brand-word{ font-size:23px; }
    .sk-hero{ padding:20px 0 44px; }
    .sk-hero-copy{ padding-top:18px; }
    .sk-badge{ margin-bottom:22px; }
    .sk-h1{ font-size:clamp(49px,15vw,64px); }
    :dir(rtl) .sk-h1{ font-size:clamp(43px,12.5vw,56px); line-height:1.04; letter-spacing:0; }
    .sk-lead{ font-size:17px; }
    .sk-cta-row{ flex-direction:column; }
    .sk-cta-row .sk-btn{ width:100%; }
    .sk-proof{ gap:9px 14px; }
    .sk-hero-media{ min-height:430px; border-radius:17px; }
    .sk-hero-media>img{ object-position:49% center; }
    .sk-live-card{ inset-inline:12px; bottom:12px; width:auto; }
    .sk-community-section{ padding:48px 0 52px; }
    .sk-section{ padding:70px 0; }
    .sk-section-heading-row{ display:block; }
    .sk-section-heading-row .sk-text-link{ margin-top:14px; }
    .sk-community-grid{ display:flex; overflow-x:auto; margin-inline:-16px; padding-inline:16px; scroll-snap-type:x mandatory; scrollbar-width:none; }
    .sk-community-grid::-webkit-scrollbar{ display:none; }
    .sk-community-card{ min-width:170px; scroll-snap-align:start; }
    .sk-feed-grid,.sk-promise-grid{ grid-template-columns:1fr; }
    .sk-feed-card:last-child,.sk-promise-card:last-child{ grid-column:auto; }
    .sk-feed-visual,.sk-audio-preview{ height:205px; }
    .sk-tool-row{ padding:18px; }
    .sk-final-section{ padding:18px 0 72px; }
    .sk-final-card{ padding:30px 22px; border-radius:19px; }
    .sk-final-action,.sk-final-action .sk-btn{ width:100%; align-items:center; }
    .sk-footer-inner{ align-items:flex-start; flex-direction:column; }
  }
  @media(prefers-reduced-motion:reduce){
    .sk-reveal,.sk-dot{ animation:none!important; opacity:1!important; transform:none!important; }
    .sk-root *{ scroll-behavior:auto!important; transition-duration:.01ms!important; }
  }
`;
